/**
 * components/landing/home/TrustBar.jsx — Section 2 (Kalki Ai Cloud).
 *
 * Animated capability counters (tick on scroll-into-view) followed by
 * one slow editorial marquee of platform credentials. Numbers reflect
 * PLATFORM CAPABILITY (never fake customer counts).
 */
import { Container } from '../design-system'
import { AnimatedCounter } from './_shared'
import { Layers, Sparkles, LineChart, Building2, ShieldCheck, Cloud } from 'lucide-react'
import { motion } from 'framer-motion'
import clsx from 'clsx'

const ITEMS = [
  { icon: Layers,      label: 'Platform modules',       to: 20, suffix: '+', tone: 'gold' },
  { icon: Sparkles,    label: 'AI features',            to: 34, suffix: '+', tone: 'gold' },
  { icon: LineChart,   label: 'Trading tools',          to: 28, suffix: '+', tone: 'emerald' },
  { icon: Building2,   label: 'Enterprise components',  to: 60, suffix: '+', tone: 'emerald' },
  { icon: ShieldCheck, label: 'White-label ready',      to: 100,suffix: '%', tone: 'gold' },
  { icon: Cloud,       label: 'Cloud infrastructure',   to: 3,  suffix: '× region', tone: 'emerald' },
]

const MARQUEE = [
  '20+ Platform Modules',
  '34+ AI Features',
  '28+ Trading Tools',
  'White-Label Ready',
  'Multi-Region Cloud',
  'Enterprise RBAC',
  'Grounded AI Mentor',
  'Backtesting Suite',
  'Sub-Second Execution',
  'GDPR Aligned',
]

const TrustBar = () => (
  <section className="relative py-16 md:py-24 border-y border-white/5">
    <div className="pointer-events-none absolute inset-0 -z-10 opacity-40"
      style={{ background: 'linear-gradient(to right, transparent, rgba(255,193,7,0.05) 30%, rgba(16,185,129,0.05) 70%, transparent)' }} />
    <Container>
      <div className="text-center text-[11px] uppercase tracking-[0.24em] text-zinc-500 font-semibold mb-10">
        Enterprise-grade AI trading infrastructure
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
        {ITEMS.map((i) => (
          <div key={i.label} className="flex flex-col items-center text-center gap-2 px-2">
            <div className={clsx(
              'h-11 w-11 rounded-xl border flex items-center justify-center',
              i.tone === 'gold' ? 'border-brand-gold/25 bg-brand-gold/10 text-brand-gold' : 'border-brand-emerald/25 bg-brand-emerald/10 text-brand-emerald',
            )}>
              <i.icon className="h-5 w-5" />
            </div>
            <AnimatedCounter
              to={i.to}
              suffix={i.suffix}
              className="text-3xl md:text-4xl font-semibold tracking-tight text-white tabular-nums"
            />
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-medium">{i.label}</div>
          </div>
        ))}
      </div>
    </Container>

    {/* One slow editorial marquee */}
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 1.2 }}
      className="relative mt-16 overflow-hidden"
      style={{ maskImage: 'linear-gradient(90deg, transparent, black 12%, black 88%, transparent)', WebkitMaskImage: 'linear-gradient(90deg, transparent, black 12%, black 88%, transparent)' }}
      data-testid="trust-marquee"
    >
      <div className="flex w-max animate-brand-marquee items-center py-2">
        {[...MARQUEE, ...MARQUEE].map((t, i) => (
          <span key={i} className="inline-flex items-center whitespace-nowrap">
            <span className="text-[12px] md:text-[13px] uppercase tracking-[0.32em] text-zinc-500 font-mono px-6">{t}</span>
            <span className="h-1.5 w-1.5 rotate-45 bg-brand-gold/70 inline-block" aria-hidden="true" />
          </span>
        ))}
      </div>
    </motion.div>
  </section>
)

export default TrustBar
